import React, {useState, useRef, useEffect} from 'react';
import {
  Card,
  Typography,
  Button,
  Input,
  List,
  Space,
  Tag,
  Upload,
  message,
  Divider,
  Empty,
  Collapse,
  Image,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  MenuOutlined,
  AudioOutlined,
  CloudUploadOutlined,
  PictureOutlined,
  LeftOutlined,
  RightOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import {
  StoryChapter,
  StoryParagraph,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/story/graphql/story.interface';
import {storyStorageRepo} from '../StoryStorageRepository';
import {ChapterService} from '@PikaDoApp/npm-pikado-logic';
import {MediaType} from '../../../utils';

const {Title, Text} = Typography;
const {Panel} = Collapse;

const isS3Url = (url: string): boolean => {
  return /^https?:\/\/(.+\.)?s3\.[a-z0-9-]+\.amazonaws\.com\//.test(url);
};

interface UnifiedContentEditorProps {
  chapters: Partial<StoryChapter>[];
  setChapters: React.Dispatch<React.SetStateAction<Partial<StoryChapter>[]>>;
  storyId: string;
}

const MediaItemPreview = ({
  m,
  onRemove,
  onMove,
  isFirst,
  isLast,
  draggable,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
  isDraggingOver,
}: {
  m: any;
  onRemove: () => void;
  onMove: (dir: 'left' | 'right') => void;
  isFirst: boolean;
  isLast: boolean;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnter?: () => void;
  onDragEnd?: () => void;
  onDrop?: (e: React.DragEvent) => void;
  isDraggingOver?: boolean;
}) => {
  const [url, setUrl] = useState(m.mediaUrl);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const resolve = async () => {
      if (!m.mediaUrl) {
        setUrl('');
        return;
      }

      if (isS3Url(m.mediaUrl)) {
        try {
          const signedUrl = await storyStorageRepo.resolveUrl(m.mediaUrl);
          setUrl(signedUrl);
        } catch (e) {
          console.error('Error resolving media URL:', e);
          setUrl(m.mediaUrl);
        }
      } else {
        setUrl(m.mediaUrl);
      }
    };
    resolve();
  }, [m.mediaUrl]);

  const {Text} = Typography;

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      onDragOver={e => e.preventDefault()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        width: '80px',
        height: '80px',
        borderRadius: '8px',
        overflow: 'hidden',
        border: isDraggingOver ? '2px dashed #1890ff' : '1px solid #cbd5e1',
        padding: '2px',
        background: '#fff',
        transition: 'all 0.2s ease',
        opacity: isDraggingOver ? 0.7 : 1,
        cursor: 'grab',
      }}>
      {m.type === MediaType.PHOTO ? (
        <Image
          src={url}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '4px',
          }}
          preview={false}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f1f5f9',
            borderRadius: '4px',
          }}>
          <Text style={{fontSize: '10px'}}>{m.type}</Text>
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.2s',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          pointerEvents: isHovered ? 'auto' : 'none',
        }}>
        <Button
          size="small"
          type="ghost"
          icon={<LeftOutlined />}
          onClick={() => onMove('left')}
          disabled={isFirst}
          style={{color: '#fff', padding: 0, border: 'none'}}
        />
        <Button
          type="primary"
          danger
          shape="circle"
          size="small"
          icon={<DeleteOutlined />}
          onClick={onRemove}
          style={{border: 'none'}}
        />
        <Button
          size="small"
          type="ghost"
          icon={<RightOutlined />}
          onClick={() => onMove('right')}
          disabled={isLast}
          style={{color: '#fff', padding: 0, border: 'none'}}
        />
      </div>
    </div>
  );
};

export const UnifiedContentEditor = ({
  chapters,
  setChapters,
  storyId,
}: UnifiedContentEditorProps) => {
  const [dragOverP, setDragOverP] = useState<{c: number; p: number} | null>(
    null,
  );
  const [dragOverM, setDragOverM] = useState<{
    c: number;
    p: number;
    m: number;
  } | null>(null);

  const chapterServiceRef = useRef<ChapterService | null>(null);
  const getChapterService = () => {
    if (!chapterServiceRef.current) {
      chapterServiceRef.current = new ChapterService();
    }
    return chapterServiceRef.current;
  };

  const addChapter = () => {
    const newChapter: Partial<StoryChapter> = {
      name: {en: '', ar: ''},
      order: chapters.length + 1,
      paragraphs: [],
    };
    setChapters([...chapters, newChapter]);
  };

  const removeChapter = (index: number) => {
    const newChapters = chapters.filter((_, i) => i !== index);
    setChapters(newChapters.map((ch, i) => ({...ch, order: i + 1})));
  };

  const updateChapter = (index: number, data: Partial<StoryChapter>) => {
    const newChapters = [...chapters];
    newChapters[index] = {...newChapters[index], ...data};
    newChapters[index].chapterDuration = newChapters[index].paragraphs.reduce(
      (sum, p) => sum + p.paragraphDuration,
      0,
    );
    setChapters(newChapters);
  };

  const moveChapter = (fromIndex: number, toIndex: number) => {
    const newChapters = [...chapters];
    const [moved] = newChapters.splice(fromIndex, 1);
    newChapters.splice(toIndex, 0, moved);
    setChapters(newChapters.map((ch, i) => ({...ch, order: i + 1})));
  };

  const transcribeAudio = async (file: File, chapterIndex: number) => {
    const loadingKey = `transcribe_${Date.now()}`;
    message.loading({content: `Transcribing ${file.name}...`, key: loadingKey});

    try {
      // 1. Upload to S3
      const audioUrl = await storyStorageRepo.uploadAudio(file, storyId);

      // 2. Transcribe using the package service
      const data = await getChapterService().transcribeAudio(file);
      let cleanedUpText = data.text.replace(/"/g, '');
      let cleanedUpWords = data.words.filter(
        w => w.word && w.word.trim() !== '',
      );

      // 3. Add as paragraph
      const newParagraph: StoryParagraph = {
        order: (chapters[chapterIndex].paragraphs?.length || 0) + 1,
        text: {en: cleanedUpText, ar: data.translatedText || ''},
        words: cleanedUpWords,
        media: [],
        paragraph_audio_url: audioUrl,
        paragraphDuration: data.duration,
      };

      const newChapters = [...chapters];
      newChapters[chapterIndex].paragraphs = [
        ...(newChapters[chapterIndex].paragraphs || []),
        newParagraph,
      ];
      newChapters[chapterIndex].chapterDuration = newChapters[
        chapterIndex
      ].paragraphs.reduce((sum, p) => sum + p.paragraphDuration, 0);
      setChapters(newChapters);

      message.success({
        content: `Successfully transcribed ${file.name}`,
        key: loadingKey,
      });
    } catch (error: any) {
      console.error('Transcription error:', error);
      message.error({
        content: `Failed to transcribe: ${error.message}`,
        key: loadingKey,
      });
    }
  };

  const moveParagraph = (
    chapterIndex: number,
    fromIndex: number,
    toIndex: number,
  ) => {
    const newChapters = [...chapters];
    const paragraphs = [...(newChapters[chapterIndex].paragraphs || [])];
    const [moved] = paragraphs.splice(fromIndex, 1);
    paragraphs.splice(toIndex, 0, moved);
    newChapters[chapterIndex].paragraphs = paragraphs.map((p, i) => ({
      ...p,
      order: i + 1,
    }));
    setChapters(newChapters);
  };

  const removeParagraph = (chapterIndex: number, pIndex: number) => {
    const newChapters = [...chapters];
    const paragraphs = newChapters[chapterIndex].paragraphs?.filter(
      (_, i) => i !== pIndex,
    );
    newChapters[chapterIndex].paragraphs = paragraphs?.map((p, i) => ({
      ...p,
      order: i + 1,
    }));
    setChapters(newChapters);
  };

  const updateParagraphText = (
    chapterIndex: number,
    pIndex: number,
    lang: 'en' | 'ar',
    value: string,
  ) => {
    const newChapters = [...chapters];
    const chapter = {...newChapters[chapterIndex]};
    const paragraphs = [...(chapter.paragraphs || [])];
    const paragraph = {...paragraphs[pIndex]};

    paragraph.text = {
      ...paragraph.text,
      [lang]: value,
    };

    paragraphs[pIndex] = paragraph;
    chapter.paragraphs = paragraphs;
    newChapters[chapterIndex] = chapter;

    setChapters(newChapters);
  };

  const uploadMedia = async (
    file: File,
    chapterIndex: number,
    pIndex: number,
  ) => {
    const loadingKey = `upload_media_${Date.now()}`;
    message.loading({content: `Uploading ${file.name}...`, key: loadingKey});

    try {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const isAudio = file.type.startsWith('audio/');

      let type: MediaType = MediaType.PHOTO;
      if (isVideo) type = MediaType.VIDEO;
      else if (isAudio) type = MediaType.AUDIO;
      else if (file.type.includes('animation') || file.name.endsWith('.json'))
        type = MediaType.ANIMATION;

      const storageType = isImage
        ? 'image'
        : isVideo
          ? 'video'
          : isAudio
            ? 'audio'
            : 'media';
      const mediaUrl = await storyStorageRepo.uploadFile(
        file,
        storyId,
        storageType,
      );

      setChapters((prev: any) => {
        const newChapters = [...prev];
        const paragraphs = [...(newChapters[chapterIndex].paragraphs || [])];
        const paragraph = {...paragraphs[pIndex]};
        const media = [...(paragraph.media || [])];

        media.push({
          mediaUrl,
          type,
          thumbnailUrl: isImage ? mediaUrl : undefined,
        });

        paragraph.media = media;
        paragraphs[pIndex] = paragraph;
        newChapters[chapterIndex].paragraphs = paragraphs;
        return newChapters;
      });

      message.success({
        content: `Successfully uploaded ${file.name}`,
        key: loadingKey,
      });
    } catch (error: any) {
      console.error('Upload media error:', error);
      message.error({
        content: `Failed to upload: ${error.message}`,
        key: loadingKey,
      });
    }
  };

  const moveMedia = (
    chapterIndex: number,
    pIndex: number,
    mIndex: number,
    direction: 'left' | 'right',
  ) => {
    const toIndex = direction === 'left' ? mIndex - 1 : mIndex + 1;
    moveMediaToIndex(chapterIndex, pIndex, mIndex, toIndex);
  };

  const moveMediaToIndex = (
    chapterIndex: number,
    pIndex: number,
    fromIndex: number,
    toIndex: number,
  ) => {
    setChapters((prev: any) => {
      const newChapters = [...prev];
      const paragraphs = [...(newChapters[chapterIndex].paragraphs || [])];
      const paragraph = {...paragraphs[pIndex]};
      const media = [...(paragraph.media || [])];

      if (toIndex < 0 || toIndex >= media.length) return prev;

      const [moved] = media.splice(fromIndex, 1);
      media.splice(toIndex, 0, moved);

      paragraph.media = media;
      paragraphs[pIndex] = paragraph;
      newChapters[chapterIndex].paragraphs = paragraphs;
      return newChapters;
    });
  };

  const removeMedia = (
    chapterIndex: number,
    pIndex: number,
    mIndex: number,
  ) => {
    setChapters((prev: any) => {
      const newChapters = [...prev];
      const paragraphs = [...(newChapters[chapterIndex].paragraphs || [])];
      const paragraph = {...paragraphs[pIndex]};
      const media = paragraph.media?.filter((_, i) => i !== mIndex);

      paragraph.media = media;
      paragraphs[pIndex] = paragraph;
      newChapters[chapterIndex].paragraphs = paragraphs;
      return newChapters;
    });
  };

  return (
    <div style={{width: '100%'}}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '32px',
          alignItems: 'flex-end',
        }}>
        <div>
          <Title level={3} style={{margin: 0}}>
            Story Content
          </Title>
          <Text type="secondary">
            Define chapters and manage paragraphs with AI transcription
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={addChapter}
          style={{
            borderRadius: '8px',
            height: '40px',
            background: 'linear-gradient(135deg, #1890ff 0%, #001529 100%)',
            border: 'none',
          }}>
          Add New Chapter
        </Button>
      </div>

      {chapters.length === 0 ? (
        <Card
          style={{
            textAlign: 'center',
            padding: '60px',
            borderRadius: '16px',
            border: '2px dashed #d9d9d9',
          }}>
          <Empty description="No chapters defined. Start by adding a chapter." />
          <Button type="link" onClick={addChapter}>
            Click here to add your first chapter
          </Button>
        </Card>
      ) : (
        <Collapse defaultActiveKey={['0']} ghost expandIconPosition="right">
          {chapters.map((chapter, cIdx) => (
            <Panel
              header={
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                    }}>
                    <Button
                      size="small"
                      type="text"
                      icon={
                        <PlusOutlined rotate={-45} style={{fontSize: '10px'}} />
                      }
                      disabled={cIdx === 0}
                      onClick={e => {
                        e.stopPropagation();
                        moveChapter(cIdx, cIdx - 1);
                      }}
                    />
                    <Button
                      size="small"
                      type="text"
                      icon={
                        <PlusOutlined rotate={45} style={{fontSize: '10px'}} />
                      }
                      disabled={cIdx === chapters.length - 1}
                      onClick={e => {
                        e.stopPropagation();
                        moveChapter(cIdx, cIdx + 1);
                      }}
                    />
                  </div>
                  <Tag color="purple">CH {chapter.order}</Tag>
                  <Tag color="purple">
                    Chapter duration: {chapter.chapterDuration}
                  </Tag>
                  <div
                    style={{display: 'flex', gap: '8px', flex: 1}}
                    onClick={e => e.stopPropagation()}>
                    <Input
                      placeholder="Chapter Title (EN)"
                      value={chapter.name?.en}
                      onChange={e =>
                        updateChapter(cIdx, {
                          name: {...chapter.name!, en: e.target.value},
                        })
                      }
                      style={{maxWidth: '250px', borderRadius: '6px'}}
                    />
                    <Input
                      placeholder="Chapter Title (AR)"
                      value={chapter.name?.ar}
                      onChange={e =>
                        updateChapter(cIdx, {
                          name: {...chapter.name!, ar: e.target.value},
                        })
                      }
                      style={{
                        maxWidth: '250px',
                        direction: 'rtl',
                        borderRadius: '6px',
                      }}
                    />
                  </div>
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={e => {
                      e.stopPropagation();
                      removeChapter(cIdx);
                    }}
                  />
                </div>
              }
              key={cIdx}
              style={{
                background: '#fff',
                marginBottom: '16px',
                borderRadius: '12px',
                border: '1px solid #f0f0f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
              }}>
              <div style={{padding: '0 12px 12px 12px'}}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}>
                  <Text strong>
                    Paragraphs ({chapter.paragraphs?.length || 0})
                  </Text>
                  <Upload
                    multiple
                    showUploadList={false}
                    beforeUpload={file => {
                      transcribeAudio(file, cIdx);
                      return false;
                    }}>
                    <Button
                      icon={<CloudUploadOutlined />}
                      type="dashed"
                      size="small">
                      Batch Upload Audio
                    </Button>
                  </Upload>
                </div>

                <div onDragOver={e => e.preventDefault()}>
                  {chapter.paragraphs?.map((para, pIdx) => (
                    <Card
                      size="small"
                      style={{
                        marginBottom:
                          dragOverP?.c === cIdx && dragOverP?.p === pIdx
                            ? '40px'
                            : '12px',
                        borderRadius: '8px',
                        border:
                          dragOverP?.c === cIdx && dragOverP?.p === pIdx
                            ? '2px dashed #1890ff'
                            : '1px solid #f0f0f0',
                        cursor: 'grab',
                        transition: 'all 0.2s ease',
                        opacity:
                          dragOverP?.c === cIdx && dragOverP?.p === pIdx
                            ? 0.6
                            : 1,
                      }}
                      key={pIdx}
                      draggable
                      onDragStart={e => {
                        e.dataTransfer.setData('pIdx', pIdx.toString());
                        e.dataTransfer.setData('cIdx', cIdx.toString());
                      }}
                      onDragEnter={() => setDragOverP({c: cIdx, p: pIdx})}
                      onDragEnd={() => setDragOverP(null)}
                      onDrop={e => {
                        e.preventDefault();
                        setDragOverP(null);
                        const fromPIdx = parseInt(
                          e.dataTransfer.getData('pIdx'),
                        );
                        const fromCIdx = parseInt(
                          e.dataTransfer.getData('cIdx'),
                        );
                        if (fromCIdx === cIdx) {
                          moveParagraph(cIdx, fromPIdx, pIdx);
                        }
                      }}>
                      <div style={{display: 'flex', gap: '16px'}}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            justifyContent: 'center',
                          }}>
                          <MenuOutlined style={{color: '#bfbfbf'}} />
                          <Text
                            type="secondary"
                            style={{fontSize: '10px', textAlign: 'center'}}>
                            {pIdx + 1}
                          </Text>
                        </div>
                        <div style={{flex: 1}}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '12px',
                              marginBottom: '8px',
                            }}>
                            <Input.TextArea
                              rows={2}
                              value={para.text.en}
                              onChange={e =>
                                updateParagraphText(
                                  cIdx,
                                  pIdx,
                                  'en',
                                  e.target.value,
                                )
                              }
                              placeholder="English Text"
                              style={{borderRadius: '6px'}}
                            />
                            <Input.TextArea
                              rows={2}
                              value={para.text.ar}
                              onChange={e =>
                                updateParagraphText(
                                  cIdx,
                                  pIdx,
                                  'ar',
                                  e.target.value,
                                )
                              }
                              placeholder="Arabic Text"
                              style={{direction: 'rtl', borderRadius: '6px'}}
                            />
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '12px',
                            }}>
                            <Space>
                              <Tag
                                icon={<AudioOutlined />}
                                color={
                                  para.paragraph_audio_url ? 'blue' : 'default'
                                }>
                                {para.paragraph_audio_url
                                  ? para.paragraph_audio_url.split('/').pop()
                                  : 'No Audio'}
                              </Tag>
                              <Tag icon={<FieldTimeOutlined />} color={'blue'}>
                                {para.paragraphDuration}
                              </Tag>
                              {para.media && para.media.length > 0 && (
                                <Tag color="purple">
                                  {para.media.length} Media items
                                </Tag>
                              )}
                            </Space>
                          </div>

                          {/* Media Section */}
                          <div
                            style={{
                              padding: '8px',
                              background: '#f8fafc',
                              borderRadius: '8px',
                              border: '1px solid #e2e8f0',
                            }}>
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '8px',
                              }}>
                              <Text type="secondary" strong>
                                <PictureOutlined /> Media
                              </Text>
                              <Upload
                                multiple
                                showUploadList={false}
                                beforeUpload={file => {
                                  uploadMedia(file, cIdx, pIdx);
                                  return false;
                                }}>
                                <Button
                                  size="small"
                                  icon={<PlusOutlined />}
                                  type="link">
                                  Add Media
                                </Button>
                              </Upload>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                gap: '8px',
                                flexWrap: 'wrap',
                              }}>
                              {para.media?.map((m, mIdx) => (
                                <MediaItemPreview
                                  key={mIdx}
                                  m={m}
                                  onRemove={() => removeMedia(cIdx, pIdx, mIdx)}
                                  onMove={dir =>
                                    moveMedia(cIdx, pIdx, mIdx, dir)
                                  }
                                  isFirst={mIdx === 0}
                                  isLast={
                                    mIdx === (para.media?.length || 0) - 1
                                  }
                                  draggable
                                  onDragStart={e => {
                                    e.dataTransfer.setData(
                                      'sourceCIdx',
                                      cIdx.toString(),
                                    );
                                    e.dataTransfer.setData(
                                      'sourcePIdx',
                                      pIdx.toString(),
                                    );
                                    e.dataTransfer.setData(
                                      'mIdx',
                                      mIdx.toString(),
                                    );
                                  }}
                                  onDragEnter={() =>
                                    setDragOverM({c: cIdx, p: pIdx, m: mIdx})
                                  }
                                  onDragEnd={() => setDragOverM(null)}
                                  onDrop={e => {
                                    e.preventDefault();
                                    const sourceCIdx = parseInt(
                                      e.dataTransfer.getData('sourceCIdx'),
                                    );
                                    const sourcePIdx = parseInt(
                                      e.dataTransfer.getData('sourcePIdx'),
                                    );
                                    const sourceMIdx = parseInt(
                                      e.dataTransfer.getData('mIdx'),
                                    );

                                    if (
                                      sourceCIdx === cIdx &&
                                      sourcePIdx === pIdx
                                    ) {
                                      moveMediaToIndex(
                                        cIdx,
                                        pIdx,
                                        sourceMIdx,
                                        mIdx,
                                      );
                                    }
                                    setDragOverM(null);
                                  }}
                                  isDraggingOver={
                                    dragOverM?.c === cIdx &&
                                    dragOverM?.p === pIdx &&
                                    dragOverM?.m === mIdx
                                  }
                                />
                              ))}
                              {(!para.media || para.media.length === 0) && (
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: '12px',
                                    fontStyle: 'italic',
                                  }}>
                                  No media items yet
                                </Text>
                              )}
                            </div>
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              marginTop: '12px',
                            }}>
                            <Button
                              type="text"
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                              onClick={() => removeParagraph(cIdx, pIdx)}
                            />
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
                <Button
                  type="dashed"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const newChapters = [...chapters];
                    const newPara: StoryParagraph = {
                      order: (newChapters[cIdx].paragraphs?.length || 0) + 1,
                      text: {en: '', ar: ''},
                      words: [],
                      media: [],
                      paragraph_audio_url: '',
                      paragraphDuration: 0,
                    };
                    newChapters[cIdx].paragraphs = [
                      ...(newChapters[cIdx].paragraphs || []),
                      newPara,
                    ];
                    setChapters(newChapters);
                  }}
                  style={{borderRadius: '8px', marginTop: '12px'}}>
                  Add Paragraph Manually
                </Button>
              </div>
            </Panel>
          ))}
        </Collapse>
      )}
    </div>
  );
};
